import { useEffect, useState } from "react";
import {
  Plus, KeyRound, RotateCcw, Trash2, Copy, Check, Loader2, ShieldCheck,
  AlertTriangle, Fingerprint, LogOut, BookOpen, Activity, XCircle, Pencil,
  Globe, Save, FileText, Calculator,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { TalentLMSRunbook } from "@/components/TalentLMSRunbook";
import { StructuredRunbook } from "@/components/StructuredRunbook";

// Iter 101 Phase 2 · Session 2 — Admin panel for the Conduit-as-OIDC-IdP.
// Super-admin lives under Settings → Integrations. Lets ops create,
// list, patch, rotate the secret of, and revoke OAuth2 client
// registrations. Also exposes the signing-key rotation control that
// backs `/api/oauth/jwks.json`.

const AVAILABLE_SCOPES = ["openid", "profile", "email", "groups", "partner_org"];

function CopyableField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Clipboard blocked"); }
  };
  return (
    // Iter 101 — `overflow-hidden` + `min-w-0` on the flex parent chain
    // stops long values (like the ~90-char discovery URL) from
    // stretching the modal past its `max-w` container.
    <div className="min-w-0 overflow-hidden rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">{label}</div>
      <div className="mt-1 flex min-w-0 items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-white/70 px-2 py-1 font-mono text-xs text-amber-900" data-testid={`${testid}-value`}>
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={doCopy} data-testid={`${testid}-copy`} className="h-7 shrink-0 border-amber-300 bg-white hover:bg-amber-100">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

export function OAuth2ClientsAdmin() {
  const [clients, setClients] = useState(null);
  const [keys, setKeys] = useState([]);
  // Iter 101 — `creating` acts as "dialog open?"; `editingId` is null for
  // create mode or the target client_id for edit mode. The form fields
  // and submit logic branch on that.
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", redirect_uris: "", scopes: ["openid", "profile", "email"], trusted: false,
    // Iter 105 — Per-client toggles. Defaults OFF so legacy peers
    // (e.g. TalentLMS which doesn't support PKCE) work out of the box.
    enforce_pkce: false, enforce_jwks: false,
    // Iter 106n — Optional SP-initiated OIDC starting endpoint. When
    // set, apps linked to this client launch by redirecting the
    // browser here; the peer then bounces back to /authorize.
    start_url: "",
    // Iter 215 — Optional peer logout URL, loaded hidden before each launch
    // so the peer drops a stale session and re-enters SSO as the current user.
    logout_url: "",
  });
  const [reveal, setReveal] = useState(null); // { client_id, client_secret, name }
  const [rotating, setRotating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [rotatingKey, setRotatingKey] = useState(false);
  // Iter 101 Phase 2 · Session 2b — Per-client "sign out all users" state.
  // Type-to-confirm modal to guard against accidental clicks.
  const [signoutTarget, setSignoutTarget] = useState(null); // { client_id, name }
  const [signoutInput, setSignoutInput] = useState("");
  const [signoutBusy, setSignoutBusy] = useState(false);
  const signoutConfirmed = signoutInput.trim().toLowerCase() === "confirm";
  const [runbookOpen, setRunbookOpen] = useState(false);
  const [structuredRunbookOpen, setStructuredRunbookOpen] = useState(false);
  // Iter 101 Phase 2 · Session 3b — OIDC connectivity self-test.
  // Loops back to Conduit's own endpoints to catch signing-key rotation,
  // misconfigured redirect URIs, PKCE regressions etc. Results are
  // rendered as a stack of green/red status cards in a modal.
  const [selftestRunning, setSelftestRunning] = useState(false);
  const [selftestResult, setSelftestResult] = useState(null);
  const [selftestOpen, setSelftestOpen] = useState(false);

  // Iter 102 — Production + Development URLs. Persisted on
  // `settings.oauth_{production,development}_url`. When either matches
  // the current request host, the backend uses it as the OIDC issuer.
  // Applies only to `/api/oauth/*` endpoints for now.
  const [endpoints, setEndpoints] = useState({ production: "", development: "" });
  const [endpointsBaseline, setEndpointsBaseline] = useState({ production: "", development: "" });
  const [endpointsSaving, setEndpointsSaving] = useState(false);
  const endpointsDirty = endpoints.production !== endpointsBaseline.production
    || endpoints.development !== endpointsBaseline.development;

  // Iter 102 — Placeholders for structured setup + Quoter integration
  // guides. Buttons stay disabled until content is written; each has
  // a "coming soon" modal so operators can preview where the docs
  // will live without a broken click surface.
  const [comingSoonKey, setComingSoonKey] = useState(null); // "structured" | "quoter" | null

  const runSelftest = async () => {
    setSelftestRunning(true);
    setSelftestResult(null);
    setSelftestOpen(true);
    try {
      const { data } = await api.post("/oauth/admin/selftest");
      setSelftestResult(data);
    } catch (e) {
      setSelftestResult({
        ok: false,
        steps: [{
          step: "HTTP request",
          status: "fail",
          detail: formatApiErrorDetail(e.response?.data?.detail) || e.message,
          duration_ms: 0,
        }],
      });
    } finally {
      setSelftestRunning(false);
    }
  };

  const load = async () => {
    try {
      const [{ data: c }, { data: k }, settingsResp] = await Promise.all([
        api.get("/oauth/admin/clients"),
        api.get("/oauth/admin/keys"),
        // Iter 102 — Fetch OIDC endpoint URLs alongside the client
        // registry. Failure is non-fatal — the fields stay blank.
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      setClients(c.clients || []);
      setKeys(k.keys || []);
      const prod = settingsResp?.data?.oauth_production_url || "";
      const dev = settingsResp?.data?.oauth_development_url || "";
      setEndpoints({ production: prod, development: dev });
      setEndpointsBaseline({ production: prod, development: dev });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load OAuth2 registry");
      setClients([]);
    }
  };
  useEffect(() => { load(); }, []);

  // Iter 102 — Persist production + development OIDC URLs. Only sends
  // the two fields to avoid clobbering unrelated settings. Trims to
  // strip trailing slashes so the backend concat with `/api/oauth`
  // stays clean.
  const saveEndpoints = async () => {
    const normalize = (u) => (u || "").trim().replace(/\/+$/, "");
    const prod = normalize(endpoints.production);
    const dev = normalize(endpoints.development);
    for (const [label, val] of [["Production URL", prod], ["Development URL", dev]]) {
      if (val && !/^https?:\/\//i.test(val)) {
        toast.error(`${label} must start with http:// or https://`);
        return;
      }
    }
    setEndpointsSaving(true);
    try {
      await api.put("/settings", {
        oauth_production_url: prod,
        oauth_development_url: dev,
      });
      toast.success("OIDC endpoint URLs saved");
      setEndpoints({ production: prod, development: dev });
      setEndpointsBaseline({ production: prod, development: dev });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setEndpointsSaving(false);
    }
  };

  const toggleScope = (s) => {
    setForm((f) => f.scopes.includes(s)
      ? { ...f, scopes: f.scopes.filter((x) => x !== s) }
      : { ...f, scopes: [...f.scopes, s] });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: "", redirect_uris: "", scopes: ["openid", "profile", "email"], trusted: false, enforce_pkce: false, enforce_jwks: false, start_url: "", logout_url: "" });
    setCreating(true);
  };
  // Iter 101 — Open the same dialog in edit mode with the client's
  // current values pre-filled. Redirect URIs stay newline-separated in
  // the textarea so operators can add/remove them the same way.
  const openEdit = (client) => {
    setEditingId(client.client_id);
    setForm({
      name: client.name || "",
      redirect_uris: (client.redirect_uris || []).join("\n"),
      scopes: client.allowed_scopes || ["openid"],
      trusted: !!client.trusted_first_party,
      enforce_pkce: !!client.enforce_pkce,
      enforce_jwks: !!client.enforce_jwks,
      start_url: client.start_url || "",
      logout_url: client.logout_url || "",
    });
    setCreating(true);
  };

  const submitForm = async () => {
    const uris = form.redirect_uris.split(/\s*,\s*|\s*\n\s*/).map((s) => s.trim()).filter(Boolean);
    if (!form.name.trim() || uris.length === 0) {
      toast.error("Name and at least one redirect URI are required");
      return;
    }
    for (const u of uris) {
      if (!/^https?:\/\//i.test(u)) {
        toast.error(`Redirect URI must start with http(s)://  → ${u}`);
        return;
      }
    }
    try {
      if (editingId) {
        // Iter 101 — Edit path. Backend PATCH endpoint accepts the same
        // schema as create; only the fields present are updated. Server
        // always forces `openid` into `allowed_scopes`, so we don't need
        // to include it explicitly here.
        await api.patch(`/oauth/admin/clients/${editingId}`, {
          name: form.name.trim(),
          redirect_uris: uris,
          allowed_scopes: form.scopes.length ? form.scopes : ["openid"],
          trusted_first_party: form.trusted,
          enforce_pkce: form.enforce_pkce,
          enforce_jwks: form.enforce_jwks,
          start_url: (form.start_url || "").trim(),
          logout_url: (form.logout_url || "").trim(),
        });
        toast.success("Client updated");
        setCreating(false);
        setEditingId(null);
        setForm({ name: "", redirect_uris: "", scopes: ["openid", "profile", "email"], trusted: false, enforce_pkce: false, enforce_jwks: false, start_url: "", logout_url: "" });
        await load();
        return;
      }
      const { data } = await api.post("/oauth/admin/clients", {
        name: form.name.trim(),
        redirect_uris: uris,
        allowed_scopes: form.scopes.length ? form.scopes : ["openid"],
        trusted_first_party: form.trusted,
        enforce_pkce: form.enforce_pkce,
        enforce_jwks: form.enforce_jwks,
        start_url: (form.start_url || "").trim(),
        logout_url: (form.logout_url || "").trim(),
      });
      setReveal({ client_id: data.client_id, client_secret: data.client_secret, name: data.name });
      setCreating(false);
      setForm({ name: "", redirect_uris: "", scopes: ["openid", "profile", "email"], trusted: false, enforce_pkce: false, enforce_jwks: false, start_url: "", logout_url: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (editingId ? "Update failed" : "Client creation failed"));
    }
  };

  const doRotate = async (client) => {
    if (!window.confirm(`Rotate secret for "${client.name}"?\n\nEvery existing SSO peer using the current secret will stop working until you paste the new secret into their configuration. Continue?`)) return;
    setRotating(client.client_id);
    try {
      const { data } = await api.post(`/oauth/admin/clients/${client.client_id}/rotate-secret`);
      setReveal({ client_id: data.client_id, client_secret: data.client_secret, name: client.name });
      toast.warning(`${data.revoked_refresh_tokens} refresh token(s) revoked · new secret ready`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rotate failed");
    } finally { setRotating(null); }
  };

  const doPatch = async (client, patch) => {
    try {
      await api.patch(`/oauth/admin/clients/${client.client_id}`, patch);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const doDelete = async (client) => {
    if (!window.confirm(`Delete OAuth2 client "${client.name}"?\n\nAll active tokens will be revoked. This cannot be undone.`)) return;
    setDeleting(client.client_id);
    try {
      await api.delete(`/oauth/admin/clients/${client.client_id}`);
      toast.success("Client deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setDeleting(null); }
  };

  const rotateSigningKey = async () => {
    if (!window.confirm("Rotate the OIDC signing key?\n\nA fresh RSA-2048 keypair will be minted. The previous key stays in JWKS long enough for outstanding id_tokens to remain verifiable (10 minutes), then retires. Continue?")) return;
    setRotatingKey(true);
    try {
      const { data } = await api.post("/oauth/admin/keys/rotate");
      toast.success(`New signing key kid=${data.kid}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Key rotation failed");
    } finally { setRotatingKey(false); }
  };

  const doSignoutAll = async () => {
    if (!signoutTarget || !signoutConfirmed || signoutBusy) return;
    setSignoutBusy(true);
    try {
      const { data } = await api.post(`/oauth/admin/clients/${signoutTarget.client_id}/revoke-all-tokens`);
      toast.warning(`Signed out ${data.revoked_tokens} session(s) + invalidated ${data.revoked_codes} pending code(s) for "${signoutTarget.name}"`);
      setSignoutTarget(null);
      setSignoutInput("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sign-out failed");
    } finally { setSignoutBusy(false); }
  };

  if (!clients) {
    return <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="oauth-clients-admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800">
            <ShieldCheck className="h-5 w-5 text-primary" />
            OAuth2 clients
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Register third-party apps (TalentLMS, HubSpot, …) that will delegate authentication to Conduit. Each client gets a
            <span className="mx-1 font-medium">client_id</span> and a one-shot <span className="font-medium">client_secret</span>.
            Discovery URL: <code className="mx-0.5 rounded bg-slate-100 px-1 font-mono text-xs">{window.location.origin}/api/oauth/.well-known/openid-configuration</code>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={runSelftest} disabled={selftestRunning} data-testid="oauth-selftest">
            {selftestRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
            Self-test
          </Button>
          <Button variant="outline" size="sm" onClick={rotateSigningKey} disabled={rotatingKey} data-testid="oauth-rotate-key">
            {rotatingKey ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Fingerprint className="h-3.5 w-3.5" />}
            Rotate signing key
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="oauth-create-client">
            <Plus className="h-3.5 w-3.5" /> New client
          </Button>
        </div>
      </div>

      {/* Iter 102 — Production + Development OIDC endpoint URLs.
          Super-admin editable. When the current request's host matches
          either URL, the backend pins the OIDC issuer (and every
          downstream discovery/JWKS/id_token field) to that URL.
          Applies only to `/api/oauth/*` endpoints for now. */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="oauth-endpoint-urls">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
              <Globe className="h-4 w-4 text-primary" />
              OIDC endpoint URLs
            </h4>
            <p className="mt-0.5 text-xs text-slate-500">
              Pin the issuer per environment so third-party apps see a stable base URL. Leave blank to fall back to the request host.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="oauth-prod-url" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Production URL</Label>
            <Input
              id="oauth-prod-url"
              data-testid="oauth-production-url-input"
              value={endpoints.production}
              onChange={(e) => setEndpoints((p) => ({ ...p, production: e.target.value }))}
              placeholder="https://conduit.example.com"
              disabled={endpointsSaving}
              className="font-mono text-xs" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="oauth-dev-url" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Development URL</Label>
            <Input
              id="oauth-dev-url"
              data-testid="oauth-development-url-input"
              value={endpoints.development}
              onChange={(e) => setEndpoints((p) => ({ ...p, development: e.target.value }))}
              placeholder="https://conduit-dev.example.com"
              disabled={endpointsSaving}
              className="font-mono text-xs" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          {endpointsDirty && (
            <span className="text-[11px] italic text-amber-700" data-testid="oauth-endpoints-dirty">Unsaved changes</span>
          )}
          <Button
            size="sm"
            onClick={saveEndpoints}
            disabled={!endpointsDirty || endpointsSaving}
            data-testid="oauth-endpoints-save">
            {endpointsSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save URLs
          </Button>
        </div>
      </div>

      {/* Signing key strip — small, informational */}
      {keys.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="oauth-signing-keys">
          <span className="font-semibold text-slate-700">Signing keys:</span>{" "}
          {keys.slice(0, 3).map((k, i) => (
            <span key={k.kid} className="ml-1">
              {i > 0 && " · "}
              <code className="rounded bg-white px-1 font-mono">{k.kid}</code>
              {k.active && !k.retired_at ? <span className="ml-1 rounded-full bg-emerald-100 px-1.5 text-[10px] font-semibold text-emerald-800">active</span>
                                          : <span className="ml-1 rounded-full bg-slate-200 px-1.5 text-[10px] font-semibold text-slate-600">retired</span>}
            </span>
          ))}
          {keys.length > 3 && <span className="ml-1 text-slate-400">+{keys.length - 3} more</span>}
        </div>
      )}

      {/* Clients list */}
      {clients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center" data-testid="oauth-no-clients">
          <KeyRound className="mx-auto h-8 w-8 text-slate-300" />
          <div className="mt-2 text-sm text-slate-500">No OAuth2 clients registered yet.</div>
          <div className="text-xs text-slate-400">Click <span className="font-semibold">New client</span> to register your first app (e.g. TalentLMS).</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="oauth-clients-list">
          {clients.map((c) => (
            <div key={c.client_id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`oauth-client-${c.client_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-heading text-sm font-semibold text-slate-800">{c.name}</div>
                    {c.trusted_first_party && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">Trusted (skip consent)</span>}
                    {c.active ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">Active</span>
                              : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Disabled</span>}
                    {/* Iter 105 — Surface the per-client security
                        posture so operators can see at a glance
                        which peers are running relaxed. */}
                    {c.enforce_pkce
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800" data-testid={`oauth-client-${c.client_id}-pkce-on`}>PKCE required</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`oauth-client-${c.client_id}-pkce-off`}>PKCE optional</span>}
                    {c.enforce_jwks
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800" data-testid={`oauth-client-${c.client_id}-jwks-on`}>RS256 / JWKS</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`oauth-client-${c.client_id}-jwks-off`}>HS256 (shared secret)</span>}
                  </div>
                  <code className="mt-1 block truncate rounded bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600" data-testid={`oauth-client-${c.client_id}-id`}>
                    {c.client_id}
                  </code>
                  <div className="mt-2 text-xs text-slate-500">
                    <div><span className="font-semibold">Scopes:</span> {(c.allowed_scopes || []).join(", ")}</div>
                    <div className="mt-0.5"><span className="font-semibold">Redirect URIs:</span> {(c.redirect_uris || []).join(" · ")}</div>
                    {c.created_by_name && <div className="mt-0.5 text-slate-400">Created by {c.created_by_name} · {new Date(c.created_at).toLocaleString()}</div>}
                    {c.secret_rotated_at && <div className="text-amber-600">Secret last rotated: {new Date(c.secret_rotated_at).toLocaleString()}</div>}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`oauth-client-${c.client_id}-edit`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => doPatch(c, { active: !c.active })} data-testid={`oauth-client-${c.client_id}-toggle`}>
                    <Switch checked={c.active} className="pointer-events-none" />
                    {/* Iter 101 — Toggle label reads as a STATE when the
                        client is on ("Enabled") and as an ACTION verb
                        when it's off ("Enable"). Prevents the previous
                        "Disable" wording, which read as if disabling
                        was the current state. */}
                    {c.active ? "Enabled" : "Enable"}
                  </Button>
                  <Button size="sm" variant="outline"
                          onClick={() => { setSignoutInput(""); setSignoutTarget({ client_id: c.client_id, name: c.name }); }}
                          data-testid={`oauth-client-${c.client_id}-signout-all`}
                          className="border-amber-300 text-amber-800 hover:bg-amber-50">
                    <LogOut className="h-3.5 w-3.5" /> Sign out all users
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => doRotate(c)} disabled={rotating === c.client_id} data-testid={`oauth-client-${c.client_id}-rotate`}>
                    {rotating === c.client_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Rotate secret
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => doDelete(c)} disabled={deleting === c.client_id} data-testid={`oauth-client-${c.client_id}-delete`} className="border-red-300 text-red-700 hover:bg-red-50">
                    {deleting === c.client_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Iter 102 — Integration guides. Grouped launcher for
          per-integration setup runbooks. Only TalentLMS is wired up
          today; Structured Setup + Quoter are placeholders (greyed
          out) that open a "coming soon" modal so operators know the
          docs are on the roadmap. */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="oauth-integration-guides">
        <div className="min-w-0">
          <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
            <BookOpen className="h-4 w-4 text-primary" />
            Integration setup guides
          </h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Step-by-step runbooks for wiring specific third-party apps to Conduit&apos;s OIDC IdP.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRunbookOpen(true)}
            data-testid="oauth-open-runbook"
            className="border-primary/40 text-primary hover:bg-primary/5">
            <BookOpen className="h-3.5 w-3.5" /> TalentLMS
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStructuredRunbookOpen(true)}
            data-testid="oauth-open-guide-structured"
            className="border-primary/40 text-primary hover:bg-primary/5">
            <BookOpen className="h-3.5 w-3.5" /> structured.ai (Marketing)
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setComingSoonKey("quoter")}
            data-testid="oauth-open-guide-quoter"
            className="border-slate-200 text-slate-500 opacity-70 hover:opacity-100"
            title="Coming soon — placeholder scaffolding">
            <Calculator className="h-3.5 w-3.5" /> Quoter
            <span className="ml-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-600">Soon</span>
          </Button>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={creating} onOpenChange={(v) => { setCreating(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-lg" data-testid="oauth-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Pencil className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
              {editingId ? "Edit OAuth2 client" : "Register OAuth2 client"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editingId
                ? "Update the redirect URIs, allowed scopes, name, or trust flag. The client_id and secret are unchanged — use Rotate secret if you need to invalidate them."
                : "The generated client_secret is shown once — copy it now, we can't retrieve it later."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="TalentLMS" data-testid="oauth-form-name" /></div>
            <div><Label>Redirect URIs</Label>
              <textarea rows={3}
                value={form.redirect_uris}
                onChange={(e) => setForm({ ...form, redirect_uris: e.target.value })}
                placeholder="https://united.talentlms.com/index/ssologin&#10;https://another.example.com/oidc/cb"
                className="min-h-[64px] w-full rounded-md border border-slate-200 bg-white p-2 font-mono text-xs shadow-sm focus:border-primary focus:outline-none"
                data-testid="oauth-form-uris" />
              <div className="mt-1 text-[11px] text-slate-400">One per line or comma-separated. Must be exact match &mdash; wildcards are not supported.</div>
            </div>
            <div>
              <Label>Allowed scopes</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {AVAILABLE_SCOPES.map((s) => (
                  <button key={s} type="button" onClick={() => toggleScope(s)} data-testid={`oauth-form-scope-${s}`}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      form.scopes.includes(s)
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                <code className="font-mono">openid</code> is always included. Uncheck others to restrict what the client can request.
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-700">Trusted first-party client</div>
                <div className="text-xs text-slate-400">Skip the consent screen. Turn on only for apps you own end-to-end (e.g. TalentLMS branded under your domain).</div>
              </div>
              <Switch checked={form.trusted} onCheckedChange={(v) => setForm({ ...form, trusted: v })} data-testid="oauth-form-trusted" />
            </div>
            {/* Iter 105 — Per-client hardening toggles. Both default
                OFF so legacy peers work out of the box; operators
                opt in per client to tighten. */}
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-700">Require PKCE (S256)</div>
                <div className="text-xs text-slate-400">Reject <code className="font-mono">/authorize</code> and <code className="font-mono">/token</code> calls without a valid <code className="font-mono">code_challenge</code>/<code className="font-mono">code_verifier</code>. Turn OFF for peers that don&apos;t support PKCE yet (e.g. TalentLMS).</div>
              </div>
              <Switch checked={form.enforce_pkce} onCheckedChange={(v) => setForm({ ...form, enforce_pkce: v })} data-testid="oauth-form-enforce-pkce" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-700">Use JWKS / RS256 signing</div>
                <div className="text-xs text-slate-400">Sign id_tokens with the RSA keypair published at <code className="font-mono">/jwks.json</code>. Turn OFF to fall back to HS256 signed with the shared <code className="font-mono">client_secret</code> for peers that don&apos;t fetch JWKS.</div>
              </div>
              <Switch checked={form.enforce_jwks} onCheckedChange={(v) => setForm({ ...form, enforce_jwks: v })} data-testid="oauth-form-enforce-jwks" />
            </div>
            {/* Iter 106n — SP-initiated OIDC starting endpoint. When
                set, any Conduit app linked to this client launches by
                redirecting the browser to this URL. The peer then
                bounces back to /authorize and the user lands in the
                app already signed in. */}
            <div className="space-y-1 rounded-md border border-slate-200 px-3 py-2">
              <label htmlFor="oauth-form-start-url" className="text-sm font-medium text-slate-700">Starting endpoint <span className="ml-1 font-normal text-slate-400">(optional)</span></label>
              <input
                id="oauth-form-start-url"
                type="url"
                value={form.start_url}
                onChange={(e) => setForm({ ...form, start_url: e.target.value })}
                placeholder="https://united.talentlms.com/plugins/openid_connect/openid_connect.php?type=login"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none"
                data-testid="oauth-form-start-url"
              />
              <div className="text-xs text-slate-400">Where Conduit sends the user&apos;s browser when they launch an app linked to this client. Leave blank when the peer initiates the OIDC handshake itself.</div>
            </div>
            <div className="space-y-1 rounded-md border border-slate-200 px-3 py-2">
              <label htmlFor="oauth-form-logout-url" className="text-sm font-medium text-slate-700">Logout endpoint <span className="ml-1 font-normal text-slate-400">(optional)</span></label>
              <input
                id="oauth-form-logout-url"
                type="url"
                value={form.logout_url}
                onChange={(e) => setForm({ ...form, logout_url: e.target.value })}
                placeholder="https://united.talentlms.com/index/logout"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none"
                data-testid="oauth-form-logout-url"
              />
              <div className="text-xs text-slate-400">Loaded in a hidden frame right before every launch so the peer drops any session it still holds (e.g. yours while you are viewing as someone else) and signs in again as the current Conduit user. Structured AI has no logout route &mdash; use <code className="font-mono">https://unitedprm.com/session-expired</code>, which runs their local sign-out on load.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={submitForm} data-testid="oauth-form-submit">
              {editingId ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret reveal dialog — shown once after create/rotate */}
      <Dialog open={!!reveal} onOpenChange={(v) => !v && setReveal(null)}>
        <DialogContent className="max-w-xl overflow-hidden" data-testid="oauth-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-4 w-4" /> Copy the client secret now</DialogTitle>
            <DialogDescription className="text-xs">
              This is the only time <span className="font-semibold">{reveal?.name}</span>&apos;s <code className="font-mono">client_secret</code> is visible. We store it hashed. If you lose it, use <span className="font-medium">Rotate secret</span> to mint a new one.
            </DialogDescription>
          </DialogHeader>
          {reveal && (
            <div className="space-y-3 pt-2">
              <CopyableField label="Client ID" value={reveal.client_id} testid="oauth-reveal-id" />
              <CopyableField label="Client Secret" value={reveal.client_secret} testid="oauth-reveal-secret" />
              <CopyableField label="Discovery URL" value={`${window.location.origin}/api/oauth/.well-known/openid-configuration`} testid="oauth-reveal-discovery" />
              <div className="text-[11px] text-slate-500">
                Paste these into the third-party app&apos;s OIDC configuration. The <b>Discovery URL</b> auto-populates issuer, authorization endpoint, token endpoint, JWKS URI and supported scopes.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setReveal(null)} data-testid="oauth-reveal-close">I&apos;ve saved it &mdash; close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 101 Phase 2 · Session 2b — Per-client mass sign-out with
          type-to-confirm. Distinct from Rotate secret (which requires
          peer reconfiguration) — this keeps the client_secret intact
          but invalidates every user's tokens for one client. */}
      <Dialog open={!!signoutTarget}
              onOpenChange={(v) => { if (!signoutBusy) { if (!v) { setSignoutTarget(null); setSignoutInput(""); } } }}>
        <DialogContent className="max-w-md" data-testid="oauth-signout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <LogOut className="h-4 w-4" /> Sign every {signoutTarget?.name} user out?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2 text-sm text-slate-600">
              <div>
                Invalidate every access + refresh token issued to <span className="font-semibold">{signoutTarget?.name}</span> so all users must re-authenticate on their next request.
              </div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-500">
                <li>The <span className="font-mono">client_secret</span> stays the same &mdash; peer configuration doesn&apos;t need to change.</li>
                <li>Pending authorization codes are also invalidated to close the entire redemption window.</li>
                <li>An audit-log entry records who triggered this and how many tokens were affected.</li>
                <li>There is no undo. Use this after a client-side security scare where session material may be exposed but the shared secret is fine.</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="oauth-signout-input" className="text-xs font-medium text-slate-700">
              Type <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-amber-700">confirm</span> to enable the button:
            </Label>
            <Input
              id="oauth-signout-input"
              data-testid="oauth-signout-input"
              value={signoutInput}
              onChange={(e) => setSignoutInput(e.target.value)}
              placeholder="confirm"
              autoComplete="off"
              disabled={signoutBusy}
              className={signoutConfirmed ? "border-amber-400 focus-visible:ring-amber-400" : ""} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setSignoutTarget(null); setSignoutInput(""); }} disabled={signoutBusy} data-testid="oauth-signout-cancel">
              Cancel
            </Button>
            <Button
              onClick={doSignoutAll}
              disabled={!signoutConfirmed || signoutBusy}
              data-testid="oauth-signout-confirm"
              className="bg-amber-600 text-white hover:bg-amber-700 disabled:bg-amber-300 disabled:hover:bg-amber-300">
              {signoutBusy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing out&hellip;</>)
                           : (<><LogOut className="h-3.5 w-3.5" /> Sign out all users</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 101 Phase 2 · Session 3 — TalentLMS OIDC setup runbook. */}
      <TalentLMSRunbook open={runbookOpen} onOpenChange={setRunbookOpen} />
      <StructuredRunbook open={structuredRunbookOpen} onOpenChange={setStructuredRunbookOpen} />

      {/* Iter 102 — Placeholder guide modal for integrations that are
          on the roadmap but not yet documented. Kept intentionally
          minimal so it's obvious this is scaffolding awaiting real
          content. */}
      <Dialog open={!!comingSoonKey} onOpenChange={(v) => !v && setComingSoonKey(null)}>
        <DialogContent className="max-w-md" data-testid="oauth-guide-comingsoon-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              {comingSoonKey === "quoter"
                ? <Calculator className="h-4 w-4" />
                : <FileText className="h-4 w-4" />}
              {comingSoonKey === "quoter" ? "Quoter integration guide" : "Structured Setup guide"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              This runbook is on the roadmap. The button + modal are wired so we can drop the step-by-step content in without touching the OAuth2 admin layout again.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid="oauth-guide-comingsoon-body">
            Content coming soon.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComingSoonKey(null)} data-testid="oauth-guide-comingsoon-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 101 Phase 2 · Session 3b — OIDC self-test results. */}
      <Dialog open={selftestOpen} onOpenChange={(v) => { if (!selftestRunning) setSelftestOpen(v); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="oauth-selftest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              OIDC connectivity self-test
              {selftestResult && (selftestResult.ok
                ? <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">PASS</span>
                : <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">FAIL</span>)}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Loops through discovery → JWKS → mint code → token exchange → id_token verify → UserInfo → refresh rotation → PKCE enforcement, all against Conduit&apos;s own endpoints. A throwaway client is created and cleaned up automatically.
              {selftestResult?.total_duration_ms != null && <span className="ml-1">Total: <span className="font-mono">{selftestResult.total_duration_ms}ms</span>.</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 space-y-2" data-testid="oauth-selftest-steps">
            {selftestRunning && (!selftestResult || selftestResult.steps.length === 0) && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Running self-test…
              </div>
            )}
            {(selftestResult?.steps || []).map((s, idx) => (
              <div key={idx}
                   data-testid={`selftest-step-${idx}`}
                   className={`flex items-start gap-3 rounded-md border px-3 py-2 ${
                     s.status === "pass"
                       ? "border-emerald-200 bg-emerald-50"
                       : "border-red-200 bg-red-50"
                   }`}>
                <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                  s.status === "pass" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
                }`}>
                  {s.status === "pass" ? <Check className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className={`font-heading text-sm font-semibold ${s.status === "pass" ? "text-emerald-900" : "text-red-900"}`}>{s.step}</div>
                    <div className={`text-[11px] font-mono ${s.status === "pass" ? "text-emerald-700" : "text-red-700"}`}>{s.duration_ms}ms</div>
                  </div>
                  <div className={`mt-0.5 text-xs ${s.status === "pass" ? "text-emerald-800/90" : "text-red-800/90"}`}>{s.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setSelftestOpen(false)} disabled={selftestRunning} data-testid="oauth-selftest-close">
              Close
            </Button>
            <Button onClick={runSelftest} disabled={selftestRunning} data-testid="oauth-selftest-rerun">
              {selftestRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Run again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
